import React, { useImperativeHandle, useRef, useState, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { addAuditRecord } from "../../../api/auditApi";
import useUserCompany from "../../../hooks/useUserCompany";
import { useSelector } from "react-redux";
import {ResizableScrollingTextRenderer} from "../../renderer/StyleRenderer";
import { formatCsvDate } from "../../../dateConfig";

const Grid = React.forwardRef((props, ref) => {
    const {
        config,
        content,
        handleDownloadPopUpChange,
        heading,
        searchable = true,
        downloadAction = "",
        downloadParam,
        checkboxSelection = true,
        showDownloadButton = true,
        customTextRenderer = ResizableScrollingTextRenderer,
        reportName
    } = props;

    const company = useUserCompany();
    const user = useSelector((state) => state.user);
    const gridRef = useRef();
    const [filterText, setFilterText] = useState("");
    const [pageSize, setPageSize] = useState(10);
    const [rowCount, setRowCount] = useState(0);

    useEffect(() => {
        if (gridRef.current && gridRef.current.api) {
            setRowCount(gridRef.current.api.paginationGetRowCount());
        }
    }, [content]);

    const dateFields = [
        "createdDate",
        "requestedArrivalTime",
        "plannedDispatchTime",
        "requestedDeliveryTime",
        "yard_flightcutoffDeliveryTime",
        "approvedDate",
        "jobConfirmedDate",
        "arrivedAtPickup",
        "departedFromPickup",
        "arrivedAtDelivery",
        "departedFromDelivery",
        "arrdatPickupPoint",
        "arrdatDeliveryPoint"
    ];

    const convertToCSV = (data, config) => {
        const header = config.map((col) => col.headerName).join(",") + "\n";
        const rows = data
            .map((item) =>
                config
                    .map((col) => {
                        let value = item[col.field] || "";
                        if (dateFields.includes(col.field) && value) {
                            value = formatCsvDate(value);
                        }
                        if (typeof value === "string" && (value.includes(",") || value.includes("\n"))) {
                            value = `"${value.replace(/"/g, '""')}"`;
                        }
                        return value;
                    })
                    .join(",")
            )
            .join("\n");
        return header + rows;
    };

    const download = async ({ fileName }) => {
        if (!content || content.length === 0) {
            alert("No data to download!");
            return;
        }

        if (!fileName) {
            fileName = "default_report.csv";
        }

        if (!fileName.endsWith(".csv")) {
            fileName += ".csv";
        }

        const selectedRows = gridRef.current.api.getSelectedRows();
        const rowsToDownload = selectedRows.length > 0 ? selectedRows : content;

        const csvData = convertToCSV(rowsToDownload, config);
        const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        link.click();

        await addAuditRecord({
            company: company ? company.comId : null,
            action: downloadAction,
            params: downloadParam,
            type: 6,
            module: 5,
            user: user?.username,
        });
    };

    useImperativeHandle(ref, () => ({
        download,
    }));

    const defaultColDef = {
        sortable: true,
        resizable: true,
        flex: 1,
        minWidth: 250,
        padding: 2,
        cellStyle: {
            textAlign: "center",
            overflow: "hidden",
            textOverflow: "ellipsis",
        },
        suppressMenu: true,
        wrapText: true,
        autoHeight: true,
    };

    const textScrollRenderer = (params) => {
        if (!params.value) return "";
        const maxLength = 20;
        const width = "210px";
        const isLongText = params.value.length > maxLength;
        return (
            <div
                style={{
                    whiteSpace: isLongText ? "nowrap" : "normal",
                    overflow: isLongText ? "hidden" : "visible",
                    textOverflow: isLongText ? "ellipsis" : "clip",
                    width: isLongText ? width : "auto",
                }}
                title={isLongText ? params.value : undefined}
            >
                {params.value}
            </div>
        );
    };

    const modifiedConfig = checkboxSelection
        ? [
            {
                headerCheckboxSelection: true,
                checkboxSelection: true,
                minWidth: 50,
            },
            ...config.map((col) => ({
                ...col,
                cellRenderer: customTextRenderer,
                cellRendererParams: {
                    threshold: 20,
                    width: "210px",
                },
            })),
        ]
        : config.map((col) => ({
            ...col,
            cellRenderer: customTextRenderer,
            cellRendererParams: {
                threshold: 20,
                width: "210px",
            },
        }));

    const onGridReady = (params) => {
        setRowCount(params.api.paginationGetRowCount());
    };

    const onPaginationChanged = () => {
        if (gridRef.current && gridRef.current.api) {
            setRowCount(gridRef.current.api.paginationGetRowCount());
        }
    };

    return (
        <div>
            <div style={{ marginTop: "5px" }}>{heading && <h2>{heading}</h2>}</div>

            {showDownloadButton && (
                <div style={{ marginBottom: "5px", textAlign: "right" }}>
                    {/*<button*/}
                    {/*    className="ui primary button"*/}
                    {/*    onClick={handleDownloadPopUpChange}*/}
                    {/*    style={{*/}
                    {/*        padding: "10px 15px",*/}
                    {/*        border: "none",*/}
                    {/*        color: "#fff",*/}
                    {/*        borderRadius: "5px",*/}
                    {/*        marginBottom: "10px",*/}
                    {/*        cursor: "pointer",*/}
                    {/*    }}*/}
                    {/*>*/}
                    {/*    Download Report*/}
                    {/*</button>*/}
                    <button
                        className="ui button"
                        onClick={() => {
                            const fromDate = downloadParam?.fromDate;
                            const toDate = downloadParam?.toDate;

                            const fileName =
                                fromDate && toDate
                                    ? `JOB REPORT FROM ${fromDate} TO ${toDate}.csv`
                                    : reportName || "report_download.csv";

                            download({ fileName });
                        }}
                        style={{
                            padding: "10px 15px",
                            border: "1px solid #2185d0",
                            color: "#2185d0",
                            borderRadius: "5px",
                            marginLeft: "10px",
                            cursor: "pointer",
                        }}
                    >
                        Download Grid Data
                    </button>

                </div>
            )}

            {searchable && (
                <input
                    type="text"
                    placeholder="Search..."
                    onChange={(e) => setFilterText(e.target.value)}
                    value={filterText}
                    style={{
                        width: "100%",
                        padding: "10px",
                        marginBottom: "10px",
                        borderRadius: "5px",
                        border: "1px solid #ccc",
                        outline: "none",
                    }}
                />
            )}

            <div className="ag-theme-alpine" style={{ height: "500px", width: "100%" }}>
                <AgGridReact
                    ref={gridRef}
                    columnDefs={modifiedConfig}
                    rowData={content}
                    defaultColDef={defaultColDef}
                    pagination={true}
                    paginationPageSize={pageSize}
                    onGridReady={onGridReady}
                    onPaginationChanged={onPaginationChanged}
                    quickFilterText={filterText}
                    rowSelection="multiple"
                    domLayout="normal"
                    suppressScrollOnNewData={true}
                    suppressRowClickSelection={true}
                    animateRows={true}
                    headerHeight={50}
                    rowHeight={50}
                />
            </div>
        </div>
    );
});

export { Grid };
